import React, { useState, useEffect } from "react";
import { Box, Autocomplete, TextField, Snackbar, IconButton, Alert, Tooltip } from '@mui/material';
import { ToggleOff, ToggleOn } from '@mui/icons-material';
import CloseIcon from "@mui/icons-material/Close";
import { OfficeLocationFilter, ProgramFilter, ReportFilterBar } from "../ReportFilters";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { DataGridPro, useGridApiRef, GridTreeDataGroupingCell, ToolbarButton } from '@mui/x-data-grid-pro';
import ControlledDataGrid from '../../DataGrid/ControlledDataGrid';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";
import dayjs from "dayjs";

const shortcutsItems = [
    {
        label: 'This Week',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('week'), today.endOf('week')];
        },
    },
    {
        label: 'Last Week',
        getValue: () => {
            const today = dayjs();
            const prevWeek = today.subtract(7, 'day');
            return [prevWeek.startOf('week'), prevWeek.endOf('week')];
        },
    },
    {
        label: 'Last 7 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(7, 'day'), today];
        },
    },
    {
        label: 'Current Month',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('month'), today.endOf('month')];
        },
    },
    {
        label: 'Last Month',
        getValue: () => {
            const today = dayjs();
            const lastMonth = today.subtract(1, 'month');
            return [lastMonth.startOf('month'), lastMonth.endOf('month')];
        },
    },
];

export default function ClientAttendanceReport(props) {
    const [gridId, setGridId] = useState(68);
    const apiRef = useGridApiRef();

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedOffice, setSelectedOffice] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [startDate, setStartDate] = useState(moment().startOf('month'));
    const [endDate, setEndDate] = useState(moment().endOf('month'));
    const [isGrouped, setIsGrouped] = useState(true);
    const [reportDetails, setReportDetails] = useState({});
    const [isFavorite, setIsFavorite] = useState(false);
    const [successText, setSuccessText] = useState("");
    const [errorText, setErrorText] = useState("");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        axios
            .get(APIURL + "/jsreport/getAllReports", {
                params: { empID: props.user.emp_id },
            })
            .then((response) => {
                const report = response?.data?.reports.find((ele) => ele.report_name === "Client Attendance Report");
                setReportDetails(report);
                setIsFavorite(report ? (report.status === 1 ? true : false) : false);
            });

        axios
            .get(APIURL + "/reports/getCustomerSessions", {
                params: {
                    compID: props.user.comp_id,
                    empID: props.user.emp_id,
                    cus_id: selectedCustomer,
                    start: moment(startDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                    end: moment(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                    oloc_id: selectedOffice || undefined,
                    program_id: selectedProgram || undefined,
                },
            })
            .then((response) => {
                setData(response?.data.length !== 0 ? response?.data : []);
                setIsLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setIsLoading(false);
            });

        axios
            .get(APIURL + "/customers/v2", {
                params: {
                    company_id: props.user.comp_id,
                },
            })
            .then((response) => {
                setCustomerList(response?.data.filter((cus) => cus.cus_status === 'Active') || []);
            });
    };

    const rows = data.map((item, idx) => {
        return {
            id: idx,
            cus_name: item.cus_name,
            tas_id: item.tas_id,
            task_name: item.tas_title,
            tas_date: moment(item.empLocdate).format('MM/DD/YYYY'),
            task_type: item.tas_type,
            tas_time_spent: Number(item.taskHrs || 0).toFixed(2),
            units_spent: Number(item.unitsSpent || 0),
            auth_units: item.authUnits ? item.authUnits : '-',
            remaining_units: item.authUnits ? (item.authUnits - Number(item.unitsSpent || 0)) : '-',
        };
    });

    const baseColumns = [
        { field: "tas_id", headerName: "Task ID", hide: true },
        { field: "task_name", headerName: "Session Name", width: 250 },
        { field: "tas_date", headerName: "Session Date", width: 150 },
        { field: "tas_time_spent", headerName: "Session Duration", width: 150 },
        { field: "units_spent", headerName: "Units Spent", width: 150 },
        {
            field: "auth_units",
            headerName: "Authorized Units",
            width: 150,
            renderCell: (params) => {
                try {
                    if (isGrouped && params.rowNode?.type === 'group') {
                        const isTaskTypeGroup = params.rowNode.depth === 1;
                        if (isTaskTypeGroup) {
                            const childRow = params.api.getRow(params.rowNode.children[0]);
                            return childRow?.auth_units ? `Total: ${childRow.auth_units}` : '';
                        }
                        return '';
                    }
                } catch (err) {
                    return params.value;
                }
                // if (isGrouped && params.rowNode?.type === 'group') {
                //     const total = sumDescendantAuthUnits(params.api, params.rowNode);
                //     return total > 0 ? `Total: ${total}` : '';
                // }
            }
        },
        { field: "remaining_units", headerName: "Units Remaining", width: 150 },
    ];

    const columns = isGrouped
        ? baseColumns
        : [
            { field: "cus_name", headerName: "Client", width: 220 },
            { field: "task_type", headerName: "Session Type", width: 160 },
            ...baseColumns,
        ];

    const sumDescendantAuthUnits = (api, node) => {
        let total = 0;
        const children = node?.children ? Array.from(node.children) : [];
        for (const childId of children) {
            const childRow = api.getRow(childId);
            if (childRow) {
                const v = Number(childRow.auth_units);
                if (!Number.isNaN(v)) total += v;
            } else if (api.getRowNode) {
                const childNode = api.getRowNode(childId);
                if (childNode) total += sumDescendantAuthUnits(api, childNode);
            }
        }
        return total;
    };

    const markFavourite = async (data, type) => {
        const payload = {
            report_id: data?.report_id,
            status: type,
            empID: props.user.emp_id
        };

        axios
            .put(APIURL + "/jsreport/markFavorite", payload)
            .then((response) => {
                if (response?.status === 200 && response?.data?.code === 200) {
                    setSuccessText(response?.data?.status_message || "Favorite Modifed Succesfully");
                } else {
                    setErrorText("Something went wrong");
                }

                fetchData();
            });
    }

    return (
        (<div style={{ width: '100%', padding: 10 }}>
            <div style={{ display: 'flex', marginBottom: 10, justifyContent: 'space-between' }}>
                <ReportFilterBar onSearch={() => fetchData()} style={{ flexWrap: 'wrap', rowGap: 10 }}>
                    <Autocomplete
                        id="client-select"
                        options={customerList}
                        getOptionLabel={(option) => option.cus_name}
                        onChange={(event, value) => setSelectedCustomer(value?.cus_id ?? null)}
                        renderInput={(params) => <TextField {...params} label="Select Client" />}
                        size="small"
                        sx={{ width: 250 }}
                    />

                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        localeText={{ start: "From Date", end: "To Date" }}
                    >
                        <DateRangePicker
                            label='Date Range'
                            value={[
                                startDate ? dayjs(startDate) : null,
                                endDate ? dayjs(endDate) : null,
                            ]}
                            onChange={(newValue) => {
                                setStartDate(newValue[0] ? new Date(newValue[0]) : null);
                                setEndDate(newValue[1] ? new Date(newValue[1]) : null);
                            }}
                            slotProps={{
                                shortcuts: {
                                    items: shortcutsItems,
                                },
                                textField: { size: 'small', style: { width: 275 } }
                            }}
                        />
                    </LocalizationProvider>

                    <OfficeLocationFilter
                        comp_id={props.user.comp_id}
                        value={selectedOffice}
                        onChange={setSelectedOffice}
                    />

                    <ProgramFilter
                        comp_id={props.user.comp_id}
                        value={selectedProgram}
                        onChange={setSelectedProgram}
                    />
                </ReportFilterBar>

                <div>
                    <Tooltip title={isFavorite ? "Remove from favorite reports" : "Add to favorite reports"}>
                        <img
                            src={isFavorite ? "./star-solid.svg" : "./star-regular.svg"}
                            alt="barchart"
                            style={{
                                fontFamily: "Lato",
                                width: "1.5rem",
                                height: "1.5rem",
                                cursor: "pointer",
                                margin: 5
                            }}
                            onClick={() => markFavourite(reportDetails, isFavorite ? 0 : 1)}
                        />
                    </Tooltip>
                </div>
            </div>
            <div style={{ height: 'calc(100vh - 250px)' }}>
                <ControlledDataGrid
                    key={isGrouped ? 'grouped' : 'flat'}
                    gridId={gridId}
                    user={props.user}
                    apiRef={apiRef}
                    getRowId={(row) => row.id}
                    rows={rows}
                    columns={columns}
                    checkboxSelection={false}
                    disableRowSelectionOnClick
                    loading={isLoading}
                    toolbarButtons={[() =>
                        <Tooltip title={isGrouped ? 'Ungroup' : 'Group'}>
                            <ToolbarButton onClick={() => setIsGrouped(!isGrouped)}>
                                {isGrouped ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
                            </ToolbarButton>
                        </Tooltip>
                    ]}
                    groupingColDef={isGrouped ? {
                        headerName: "Client",
                        width: 300,
                        renderCell: (params) => {
                            const isTaskTypeGroup = params.rowNode?.type === 'group' && params.rowNode?.depth === 1;

                            let totalUnits = 0;
                            if (isTaskTypeGroup) {
                                const children = params.rowNode.children ? Array.from(params.rowNode.children) : [];

                                for (const childId of children) {
                                    const childRow = params.api.getRow(childId);
                                    if (childRow) {
                                        totalUnits += Number(childRow.units_spent || 0);
                                    }
                                }
                            }
                            return (
                                <div style={{ display: 'flex' }}>
                                    <GridTreeDataGroupingCell {...params} />
                                    {isTaskTypeGroup && (
                                        <span style={{ marginLeft: 10, fontWeight: 'bold' }}>
                                            {`(Unit Total: ${totalUnits.toFixed(2)})`}
                                        </span>
                                    )}
                                </div>
                            );
                        },
                    } : undefined}
                    treeData={isGrouped}
                    getTreeDataPath={(row) => isGrouped ? [row.cus_name, row.task_type, `(${row.id}) - ` + row.tas_id] : undefined}
                    sx={{
                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            textAlign: 'left',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        },
                    }}
                />
            </div>
            <Snackbar
                open={successText !== ""}
                autoHideDuration={30000}
                onClose={() => setSuccessText("")}
            >
                <Alert
                    sx={{ width: "100%" }}
                    severity={"success"}
                    elevation={6}
                    variant="filled"
                >
                    {successText}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={() => setSuccessText("")}
                    >
                        <CloseIcon />
                    </IconButton>
                </Alert>
            </Snackbar>
            <Snackbar
                open={errorText !== ""}
                autoHideDuration={30000}
                onClose={() => setErrorText("")}
            >
                <Alert
                    sx={{ width: "100%" }}
                    severity={"error"}
                    elevation={6}
                    variant="filled"
                >
                    {errorText}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={() => setErrorText("")}
                    >
                        <CloseIcon />
                    </IconButton>
                </Alert>
            </Snackbar>
        </div>)
    );
}